import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Collapse, Grid, Typography, useMediaQuery, useTheme, Accordion, AccordionSummary, AccordionDetails, Link, MenuItem, ListItemIcon, IconButton } from "@mui/material";
import {
  AddCircleOutlineOutlined, DeleteOutlineOutlined, DoDisturbAltOutlined, DriveFileRenameOutline,
  FormatItalicOutlined, SearchOutlined, VisibilityOutlined, DriveFolderUpload,
  PictureAsPdfOutlined,
  Cancel,
  Close,
  KeyboardArrowDown,
  ExitToApp,
  DownloadOutlined,
  CloudDownload,
  TrackChanges // CS-2070
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ProductInfo } from "../../../redux/slice/product/productListSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { SingleAlertInfo, fetchManufacturersListSkipLoading, setSingleAlertObj, showHideLoadingDialog, useCommonReducer } from "../../../redux/slice/commonSlice";
import {
  PracticeProductAgreement, SKUProductAgreementRequest, SearchPracticeProductAgreementRequest, bulkInactivateProductAgreementByIds, createMassAgreements, deleteProductAgreementById, fetchProductAgreementList,
  fetchProductListIncludingInactiveByUserRoleAgreementSlice,
  fetchSKUProductAgreementList,
  generateNewAgreementsForExpiredProductPrice, getAgreementCountForExpiredProductPrice, getPracticeListByUserRoleAgreementSlice, inactivateProductAgreementsById,
  initiateProductAgreements, previewAgreement, resetProductAgreementsList, useCreateAgreementSlice
} from "../../../redux/slice/productAgreement/createAgreementSlice";
import Button from "../../common/Button";
import { getPracticeListforFilter, PracticeInfo, usePracticeSlice } from "../../../redux/slice/practiceSlice";
import DatePickerPopup from "../../common/DatePickerPopup";
import Header from "../../common/Header";
import Table, {Row as TableRow} from "../../common/CustomTable";
import Card from "../../common/Card";
import PDFViewer from "../../common/pdf/PDFViewer";
import CustomCheckbox from "../../common/CustomCheckBox";
import CustomMultipleDropDown from "../../common/CustomMultipleDropDown";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import { selectIsTokenAvailable, selectIsValueFoundInToken, useAuthSlice } from "../../../redux/slice/authSlice";
import { hideElement, sortNames } from "../../../utils/Utility";
import { createListSort } from "../../../utils/listSort";
import {
  AGREEMENT_ACTIONS, AGREEMENT_STATUS, CAN_ACCESS_APPLICATION, EXPORTABLE_ITEMS, PRODUCT_AGREEMENT_STATUS_LIST,
  PRODUCT_AGREEMENT_TYPE_MULTI, PRODUCT_AGREEMENT_TYPE_SINGLE, PRODUCT_AGREEMENT_VALIDITY_LIST,
  RESPONSE_CODES, USER_ROLE_ENTITY_NAMES, USER_ROLES
} from "../../../constants/applicationConstants";
import LegacyToolBar from "../../common/LegacyToolBar";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import CustomIconButton from "../../common/CustomIconButton";
import { CodeTable, fetchAllCodeTablesValues, getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import dayjs from "dayjs";
import { getSearchCriteriaFromSessionStorage, saveSelectionForPage, setSearchCriteriaToSessionStorage } from "../../../utils/StorageUtils";
import UploadSignedAgreementPopup from "./UploadSignedAgreementPopup";
import { useNavigation } from "../../../hooks/useNavigation";
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
import { tokens } from "../../../theme";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import ProductAgreementPopup from "../product/ProductAgreementPopup";
import StyledMenu from "../../common/StyledMenu";
import ExportToExcel from "../../common/ExportToExcel";
import { fetchManufacturerListByRole, useManufacturerListSlice } from "../../../redux/slice/manufacturer/ManufacturerSlice";
import { useModalStyles } from "../../common/useModalStyles";
import AuditLogDisplayPopup, { AuditLogData } from "../practice/AuditLogDisplayPopup"; // CS-2070
import { fetchAuditLogList } from "../../../redux/slice/auditLog/auditLogSlice"; // CS-2070

interface ProductAgreement {
  effectiveDate: string;
  inactiveDate: string;
  product: string;
  practice: string;
  pricingType: string;
  discount: string;
  mac: string;
  discountPrice: string;
};
interface Row {
  checkboxes: JSX.Element;
  productId: number;
  practiceProductId: number,
  product: string;
  productName?: string;
  inactiveDate: string;
  practice: string;
  effectiveDate: string;
  fileType: string;
  discountType: string;
  extendedPrice: JSX.Element | string;
  agreementType: string;
  status: string;
  auditTimestamp : string;
  action?: JSX.Element;
  provider: string;
  // CS-1628
  canDeleteAgreement?: boolean;
  agreementGroupId?: string | null;
};

type Option = {
  value: string,
  label: string
};

const ITEMS_PER_PAGE = 20;

const agreementListSort = createListSort(
  ["productName", "manufacturerName", "practiceName", "providerName", "effectiveDate", "inactiveDate", "extendedPrice", "agreementStatus", "auditTimestamp"] as const,
  {
    product: "productName",
    manufacturerIds: "manufacturerName",
    practice: "practiceName",
    provider: "providerName",
    effectiveDate: "effectiveDate",
    inactiveDate: "inactiveDate",
    extendedPrice: "extendedPrice",
    status: "agreementStatus",
    auditTimestamp: "auditTimestamp",
  }
);

const agreementCheckboxSx = {
  width: 28,
  lineHeight: 0,
  "& .MuiFormControlLabel-root": { mr: 0, ml: 0 },
  "& .MuiCheckbox-root": { p: "2px" },
};

// CS-1288
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any>; },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});
//
const ProductAgreement: React.FC = () => {

  const location = useLocation();
  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens: modalTokens,
    primaryBtnSx,
    paperSx,
    headerSx,
    bodySx,
    footerSx,
    iconBoxSx,
    closeIconSx,
    cancelBtnSx,
  } = useModalStyles();
  const { control, handleSubmit, reset, setValue, getValues } = useForm<SearchPracticeProductAgreementRequest>();

  const [showInactivePopup, setShowInactivePopup] = useState<{ isShow: boolean; productAgreementObject: Partial<Row> }>({ isShow: false, productAgreementObject: {} });
  const [showPDF, setShowPDF] = useState<{isShow:boolean, productName: string, fileName: string}>({isShow:false, productName: "", fileName: ""});
  const [showCard, setShowCard] = useState(false);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);
  const [formattedPracticeInfo, setFormattedPracticeInfo] = useState<Row[]>();
  const [isCheck, setIsCheck] = useState<number[]>([]);
  const [isCheckAll, setIsCheckAll] = useState(false);
  const [showOptionPopup, setShowOptionsPopup] = useState<OptionPopupObjectProps>({message:'', show:false});
  const [showUploadSignedAgreementPopup, setShowUploadSignedAgreementPopup] = useState<{ show: boolean; practiceProductId: number | null }>({ show: false, practiceProductId: null });
  const [showBulkInactivePopup, setShowBulkInactivePopup] = useState<{isShow: boolean, ids: number[]}>({isShow: false, ids: []});
  // CS-2070 - Per-agreement audit trail popup (Super Admin / Admin only)
  const [showAuditLogPopup, setShowAuditLogPopup] = useState(false);
  const [auditLogData, setAuditLogData] = useState<AuditLogData[]>([]);
  const [auditLogTitle, setAuditLogTitle] = useState<string>("Audit Trail");
  const [showSkuDialog, setShowSkuDialog] = useState(false);
  const [showProductAgreementPopup, setShowProductAgreementPopup] = useState(false);
  const [skuDialogTitle, setSkuDialogTitle] = useState<string>("SKU Price Details");
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);
  const { practiceByUserRole } = usePracticeSlice();

  const [searchCriteria, setSearchCriteria] = useState<SearchPracticeProductAgreementRequest>({
    agreementIds:[],
    productIds: [],
    practiceIds: [],
    agreementStatus: [],
    agreementValidity: [],
    currentPage: 1,
    manufacturerIds: [], //CS-1625
    agreementTypes: [],
    itemsPerPage: ITEMS_PER_PAGE
  });

  const [activeButtonId, setActiveButtonId] = useState<string | null>(null); // CS-1555

  const { userInfo } = useAuthSlice();
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const { document } = useCreateAgreementSlice();
  const { productAgreementsList, productsByUserRoleAgreementSlice, skuProductAgreementList } = useCreateAgreementSlice();

  const canPracticeSignAgreement = useSelector((state: RootState) => selectIsTokenAvailable("canPracticeSignAgreement")(state));
  const canGenerateAgreements = useSelector((state: RootState) => selectIsTokenAvailable("canGenerateAgreements")(state));
  const canManufacturerSignAgreement = useSelector((state: RootState) => selectIsTokenAvailable("canManufacturerSignAgreement")(state));
  const canDeleteAgreement = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessProductAgreementActions", AGREEMENT_ACTIONS.DELETE)(state));
  const canPreviewAgreement = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessProductAgreementActions", AGREEMENT_ACTIONS.PREVIEW)(state));
  const canInitiateAgreement = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessProductAgreementActions", AGREEMENT_ACTIONS.INITIATE)(state));
  const canInactivateAgreement = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessProductAgreementActions", AGREEMENT_ACTIONS.INACTIVATE)(state));
  const canUploadAgreement = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessProductAgreementActions", AGREEMENT_ACTIONS.AGREEMENT)(state));
  const canViewAgreementAudit = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessProductAgreementActions", AGREEMENT_ACTIONS.VIEW_AUDIT)(state)); // CS-2070
  const canExportList = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessExportExcel", EXPORTABLE_ITEMS.AGREEMENT)(state));
  //CS-1625
  const { manufacturerInfoListByUserRole } = useManufacturerListSlice();

  // ─── Design tokens ───────────────────────────
  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listPanelBg    = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const listHoverBg    = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

  const listCardSx = {
    borderRadius: "14px",
    border: `1px solid ${listBorderColor}`,
    backgroundColor: listPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const filterPanelSx = {
    ...listCardSx,
    p: { xs: "14px !important", md: "18px !important" },
    position: "relative",
    zIndex: 20,
    overflow: "visible",
    "& .MuiAutocomplete-root, & .MuiFormControl-root": { width: "100%" },
    "& .MuiTextField-root": { height: "42px", minHeight: "42px !important" },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
      minHeight: "42px !important",
      height: "42px !important",
      borderRadius: "10px !important",
      backgroundColor: `${modalTokens.inputBg} !important`,
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      alignItems: "center",
      boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": { padding: "0 38px 0 12px !important" },
    "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": { paddingLeft: "12px !important", paddingRight: "12px !important" },
    "& .MuiAutocomplete-endAdornment": { top: "50% !important", transform: "translateY(-50%)", right: "8px !important" },
    "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset": { borderColor: `${modalTokens.inputBorder} !important` },
    "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: `${modalTokens.accent} !important`, borderWidth: "1px !important" },
    "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)" },
    "& .MuiInputBase-input": { color: modalTokens.textPrimary, fontSize: "0.88rem" },
    "& input::selection": { color: modalTokens.textPrimary, backgroundColor: isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.2)" },
    "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": { backgroundColor: "#376c9f !important", "& fieldset": { borderColor: "#7fb1df !important" } },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": { WebkitTextFillColor: "#ffffff", caretColor: "#ffffff", WebkitBoxShadow: "0 0 0 100px #376c9f inset", borderRadius: "10px", transition: "background-color 5000s ease-in-out 0s" },
    "& .MuiInputLabel-root": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", fontSize: "0.88rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: `${modalTokens.accent} !important` },
    "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 9px) scale(1)" },
    "& .MuiChip-root": { borderRadius: "999px", backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.1)", color: modalTokens.accent, border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(20,184,166,0.18)"}`, fontWeight: 600 },
    "& .MuiSvgIcon-root": { color: modalTokens.textSecondary },
  };

  const filterFieldLabelSx = {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: modalTokens.textSecondary,
    mb: 0.7,
    height: "16px",
    lineHeight: "16px",
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#fff" },
  };

  const initiateAgreementBtnSx = {
    ...primaryActionBtnSx,
    backgroundColor: "#6fc36a",
    borderColor: "#6fc36a",
    color: "#ffffff",
    "& .MuiTypography-root": { color: "#ffffff", fontWeight: 700 },
    "& .MuiButton-startIcon, & svg": { color: "#ffffff" },
    "&:hover": {
      backgroundColor: "#5cb85c",
      borderColor: "#5cb85c",
      color: "#ffffff",
      "& .MuiTypography-root": { color: "#ffffff" },
      "& .MuiButton-startIcon, & svg": { color: "#ffffff" },
    },
  };

  const outlineBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap",
    "&:hover": { color: "#fff", borderColor: "#00B8D9", backgroundColor: "#00B8D9", boxShadow: "0 6px 22px rgba(0,184,217,0.28)" },
  };

  const inactiveAgreementBtnSx = {
    ...outlineBtnSx,
    backgroundColor: "#f5a623",
    borderColor: "#f5a623",
    color: "#ffffff",
    "& .MuiTypography-root": { color: "#fbfcff", fontWeight: 700 },
    "& .MuiButton-startIcon, & svg": { color: "#ffffff" },
    "&:hover": {
      backgroundColor: "#e89a1f",
      borderColor: "#e89a1f",
      color: "#ffffff",
      boxShadow: "0 6px 22px rgba(245,166,35,0.28)",
      "& .MuiTypography-root": { color: "#ffffff" },
      "& .MuiButton-startIcon, & svg": { color: "#ffffff" },
    },
  };

  const exportBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#14B8A6",
    borderColor: "rgba(20,184,166,0.55)",
    backgroundColor: theme.palette.mode === "dark" ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
    whiteSpace: "nowrap",
    "& .MuiSvgIcon-root": {
      color: "#14B8A6",
    },
    "&:hover": {
      color: "#fff",
      borderColor: "#14B8A6",
      backgroundColor: "#14B8A6",
      boxShadow: "0 6px 22px rgba(20,184,166,0.28)",
      "& .MuiSvgIcon-root": {
        color: "#fff",
      },
    },
  };

  const exportMenuItemBtnSx = {
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "8px",
    py: 1,
    px: 1.4,
    color: "#2dd4bf",
    borderColor: "rgba(45,212,191,0.35)",
    backgroundColor: isDark ? "rgba(20,184,166,0.13)" : "rgba(20,184,166,0.08)",
    boxShadow: "none",
    "& .MuiButton-startIcon": { mr: 1, "& svg": { fontSize: "18px" } },
    "&:hover": { color: "#fff", borderColor: "#2f9b8f", backgroundColor: "#2f9b8f", boxShadow: "0 10px 24px rgba(20,184,166,0.28)" },
    "&.Mui-disabled": { color: isDark ? "rgba(148,163,184,0.55)" : "rgba(100,116,139,0.7)", borderColor: isDark ? "rgba(148,163,184,0.18)" : "rgba(100,116,139,0.24)", backgroundColor: isDark ? "rgba(148,163,184,0.06)" : "rgba(100,116,139,0.06)" },
  };

  const agreementTableSx = {
    "& .MuiTableContainer-root": { borderRadius: "12px", border: "none", backgroundColor: "transparent" },
    "& table": {  borderCollapse: "collapse", borderSpacing: 0 },
    "& .MuiTableCell-root": { borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`, py: 1.15, fontSize: "0.84rem" },
    "& .MuiTableHead-root .MuiTableCell-root": { height: "42px"  },
    "& .MuiTableHead-root .MuiTypography-root": { color: "#00B8D9 !important", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.02em" },
    "& .MuiTableBody-root .MuiTableRow-root": { transition: "background-color 0.18s ease, box-shadow 0.18s ease" },
    "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: `${listHoverBg} !important` },
    "& .MuiTableBody-root .MuiTableCell-root, & .MuiTableBody-root .MuiTypography-root": { color: modalTokens.textPrimary, fontSize: "0.84rem" },
    "& .MuiTableBody-root .MuiLink-root": { color: "#00B8D9 !important", fontWeight: 600, textDecorationColor: "rgba(0,184,217,0.45)" },
    "& .MuiTableBody-root .MuiLink-root:hover": { color: "#22d3ee !important", textDecorationColor: "#22d3ee" },
    "& .MuiIconButton-root": { color: isDark ? "#94a3b8" : "#475569", borderRadius: "8px", "&:hover": { color: modalTokens.accent, backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)" } },
    "& .MuiPaginationItem-root": { borderColor: listBorderColor, color: modalTokens.textSecondary, borderRadius: "8px", "&.Mui-selected": { color: "#fff", backgroundColor: modalTokens.accent, borderColor: modalTokens.accent } },
  };
  // ─────────────────────────────────────────────────────────────────────────────

  // const { manufacturerInfoList } = useCommonReducer();
  const statusOptions = useMemo(() => {
    let statuses: CodeTable[] = [];
    if (allCodeTablesValuesMap) {
      statuses = getCodeTableValuesByTableName(allCodeTablesValuesMap, PRODUCT_AGREEMENT_STATUS_LIST);
    }
    if (!statuses || statuses.length === 0) {
      dispatch(fetchAllCodeTablesValues());
    }
    let options: { label: string, value: any }[] = [];
    statuses?.map((option) => {
      options.push({ label: option.displayValue, value: option.displayValue });
    });
    
    if (userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN) {
      options = options.filter(option => option.label !== AGREEMENT_STATUS.DELETED);
    }
  
    if (userInfo.userRole === USER_ROLES.ROLE_PRACTICEADMIN) {
      options = options?.filter(option => (option.label !== AGREEMENT_STATUS.HELD));
    } else if (userInfo.userRole === USER_ROLES.ROLE_MANUFACTURERADMIN) {
      options = options?.filter(option => (option.label !== AGREEMENT_STATUS.HELD
        && option.label !== AGREEMENT_STATUS.INITIATED));
    }
    return options;
  }, [allCodeTablesValuesMap]);

  const agreementTypeOptions = useMemo(
    () => [
      { label: "Single Product", value: PRODUCT_AGREEMENT_TYPE_SINGLE },
      { label: "Multi Product", value: PRODUCT_AGREEMENT_TYPE_MULTI },
    ],
    []
  );
  //CS-1625
  useEffect(() => {
    const fechManufacturers = async () => {
      const res = await dispatch(fetchManufacturerListByRole({skipLoader: true}));
      // const res = await dispatch(fetchManufacturersListSkipLoading({ skipLoader: true }));
    }
    fechManufacturers();
  }, []);

    // CS-1288
    const validityOptions = useMemo(() => {
    let statuses: CodeTable[] = [];
    if (allCodeTablesValuesMap) {
      statuses = getCodeTableValuesByTableName(allCodeTablesValuesMap, PRODUCT_AGREEMENT_VALIDITY_LIST);
    }
    if (!statuses || statuses.length === 0) {
      dispatch(fetchAllCodeTablesValues());
    }
    let options: { label: string, value: any }[] = [];
    statuses?.map((option) => {
      options.push({ label: option.displayValue, value: option.displayValue });
    });
  
    return options;
  }, [allCodeTablesValuesMap]);
  //
  
  const practiceOptions = useMemo(() => {
    let options: Option[] = [];
    if (practiceByUserRole && practiceByUserRole.length > 0) {
      if (practiceByUserRole.length === 1) {
        setValue('practiceIds', [Number(practiceByUserRole[0]?.practiceId)]);
      }
      const sortedPracticeInfo: PracticeInfo[] = sortNames(practiceByUserRole, 'practiceName');
      sortedPracticeInfo?.map(practice => {
        options.push({ value: practice.practiceId, label: practice.practiceName });
      });
    };
    return options;
  }, [practiceByUserRole]);

  const productOptions = useMemo(() => {
    let options: Option[] = [];
    if (productsByUserRoleAgreementSlice && productsByUserRoleAgreementSlice.length > 0) {

      const activeProducts = productsByUserRoleAgreementSlice.filter((product) => {
        return product.productStatus === "ACTIVE"
      });

      if (activeProducts.length === 1) {
        setValue('productIds', [Number(activeProducts[0]?.productId)]);
      }
      const sortedProductList: ProductInfo[] = sortNames(activeProducts, 'productName');
      sortedProductList?.map((option) => {
        options.push({ label: option.productName, value: option.productId })
      });
    };
    return options;
  }, [productsByUserRoleAgreementSlice]);

  //CS-1625
  const manufacturesList = useMemo(() => {
      let options: { label: string; value: any }[] = [];
  
      if(manufacturerInfoListByUserRole && manufacturerInfoListByUserRole.length > 0){
        manufacturerInfoListByUserRole.forEach((item) => {
          options.push({
            label: item.manufacturerName,
            value: item.manufacturerId,
          });
        });
      }
      return options;
    }, [manufacturerInfoListByUserRole]);

  //Mount
  useEffect(() => {
    dispatch(showHideLoadingDialog(true))
    fetchInitialLists();
  }, []);

  const fetchInitialLists = async () => {
    try {
      dispatch(showHideLoadingDialog(true));
      //Don't remove below unused variables
      const responsePractices = await dispatch(getPracticeListforFilter());
      const responseProducts = await dispatch(fetchProductListIncludingInactiveByUserRoleAgreementSlice({includeProductPrice: false, executedOnly: false}));
    } catch (error) {
      console.error("Error fetching initial lists:", error);
    } finally {
      dispatch(showHideLoadingDialog(false));
    }
  }

  //Unmount
  useEffect(() => () => {
    dispatch(resetProductAgreementsList());
  }, []);

  useEffect(() => {
    //1. When login another user pagination is remain in the history and can redirect that page
    if (productsByUserRoleAgreementSlice && productsByUserRoleAgreementSlice.length > 0) {
      let criteria = getSearchCriteriaFromSessionStorage(CAN_ACCESS_APPLICATION.PRODUCT_AGREEMENT + "SearchCriteria" + userInfo.userId);
      if ((location.state && location.state.agreementListSearchCriteria) || (location.state && location.state.withSearchHistory && criteria)) {
        if (location.state && location.state.withSearchHistory) {
          //Below navigate line call back to this useEffect - This for refreshing issue when navigate with state.withSearchHistory=true
          navigate("/practice_product_agreement", { state: { agreementListSearchCriteria: criteria } });
          return;
        }
        let searchCriteria: SearchPracticeProductAgreementRequest = {
          agreementIds: [],
          productIds: [],
          manufacturerIds: [], //CS-1625
          practiceIds: [],
          agreementStatus: [],
          agreementValidity: [],
          agreementTypes: [],
          currentPage: 0,
          itemsPerPage: 0
        };
        if (location.state && location.state.agreementListSearchCriteria) {
          const navigationStateCriteria = location.state.agreementListSearchCriteria;
          let practiceIds: number[] | null = null;
          if (practiceByUserRole && practiceByUserRole.length === 1) {
            practiceIds = [Number(practiceByUserRole[0]?.practiceId)];
          };

          searchCriteria = agreementListSort.normalize({
            ...navigationStateCriteria,
            practiceIds: practiceIds || navigationStateCriteria.practiceIds || []
          });
        } else {
          searchCriteria = agreementListSort.normalize(criteria ? criteria : searchCriteria);
        }
        setSearchCriteria(searchCriteria);
        setValuesToCriteriaFields(searchCriteria);
        
        searchAgreementsList(searchCriteria);
      } else {
        loadInitialAgreementList(1);
      }
    }
  }, [location.state, productsByUserRoleAgreementSlice]);

  const setValuesToCriteriaFields = (searchCriteria: SearchPracticeProductAgreementRequest) => {
    setValue("practiceIds", searchCriteria.practiceIds);
    setValue("productIds", searchCriteria.productIds);
    setValue("agreementStatus", searchCriteria.agreementStatus);
    setValue("manufacturerIds", searchCriteria.manufacturerIds); //CS-1625
    setValue("agreementTypes", searchCriteria.agreementTypes || []);
  }

  const loadInitialAgreementList = (pNumber: number = 1) => {
    const practiceIdList = practiceByUserRole && practiceByUserRole.length > 0
      ? practiceByUserRole?.map(practice => Number(practice.practiceId)) : [];
    const productIdList = productsByUserRoleAgreementSlice && productsByUserRoleAgreementSlice.length > 0
      ? productsByUserRoleAgreementSlice?.map(product => Number(product.productId)) : [];
    const data: SearchPracticeProductAgreementRequest = {
      agreementIds: [],
      practiceIds: practiceIdList && practiceIdList.length === 1 ? practiceIdList : [],
      productIds: productIdList && productIdList.length === 1 ? productIdList : [],
      agreementStatus: [],
      agreementValidity: [],
      agreementTypes: [],
      currentPage: pNumber,
      itemsPerPage: ITEMS_PER_PAGE,
    };
    setSearchCriteria(data);
    setValuesToCriteriaFields(data);
    
    searchAgreementsList(data);
  }

  const searchAgreementsList = (searchCriteria: SearchPracticeProductAgreementRequest) => {
    const normalizedCriteria = agreementListSort.normalize(searchCriteria);
    setSearchCriteriaToSessionStorage(CAN_ACCESS_APPLICATION.PRODUCT_AGREEMENT + "SearchCriteria" + userInfo.userId, normalizedCriteria);

    let dataToSearch = {
      ...normalizedCriteria
    }
    if (!dataToSearch.practiceIds || dataToSearch.practiceIds.length === 0) {
      dataToSearch.practiceIds = practiceByUserRole && practiceByUserRole.length > 0
        ? practiceByUserRole?.map(practice => Number(practice.practiceId)) : [];
    }
    if (!dataToSearch.productIds || dataToSearch.productIds.length === 0) {
      dataToSearch.productIds = productsByUserRoleAgreementSlice && productsByUserRoleAgreementSlice.length > 0
        ? productsByUserRoleAgreementSlice?.map(product => Number(product.productId)) : [];
    }
    //CS-1625
    if (!dataToSearch.manufacturerIds || dataToSearch.manufacturerIds.length === 0) {
      dataToSearch.manufacturerIds = manufacturerInfoListByUserRole && manufacturerInfoListByUserRole.length > 0
        ? manufacturerInfoListByUserRole?.map(manufacturer => Number(manufacturer.manufacturerId)) : [];
    }
    if (!dataToSearch.agreementStatus || dataToSearch.agreementStatus.length === 0) {
      dataToSearch.agreementStatus = statusOptions && statusOptions.length > 0 ? statusOptions?.map(status => status.value) : [];
    }
    if (!dataToSearch.agreementValidity || dataToSearch.agreementValidity.length === 0) {
      dataToSearch.agreementValidity = validityOptions && validityOptions.length > 0 ? validityOptions?.map(status => status.value) : [];
    }

    dispatch(fetchProductAgreementList(dataToSearch));
    setIsCheck([]);
    setIsCheckAll(false);
  }

  const onSubmit = (data: SearchPracticeProductAgreementRequest) => {
    const data2: SearchPracticeProductAgreementRequest = {
      agreementIds: data.agreementIds,
      practiceIds: data.practiceIds,
      productIds: data.productIds,
      agreementStatus: data.agreementStatus,
      agreementValidity: data.agreementValidity,
      manufacturerIds:data.manufacturerIds, //CS-1625
      agreementTypes: data.agreementTypes,
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
      ...agreementListSort.pick(searchCriteria),
    };
    setSearchCriteria(data2);

    //Below navigate line call to useEffect[location.state, productsByUserRoleAgreementSlice] and search
    navigate("/practice_product_agreement", { state: { agreementListSearchCriteria: data2 } });
  }

  const handlePagination = (pageNumber: number) => {
    const data = {
      ...searchCriteria,
      currentPage: pageNumber + 1
    };
    setSearchCriteria(data);

    //Below navigate line call to useEffect[location.state, productsByUserRoleAgreementSlice] and search
    navigate("/practice_product_agreement", { state: { agreementListSearchCriteria: data } });
  }

  const handleTableSort = (columnId: string, order: "asc" | "desc") => {
    const data = agreementListSort.withSort({
      ...searchCriteria,
      itemsPerPage: searchCriteria.itemsPerPage || ITEMS_PER_PAGE,
    }, columnId, order);
    if (!data) {
      return;
    }
    setSearchCriteria(data);
    navigate("/practice_product_agreement", { state: { agreementListSearchCriteria: data } });
  }

  const handleInactiveAction = (row: Row) => {
    setShowInactivePopup({isShow: true, productAgreementObject: row});
  }

  const handleDeleteAction = async (row: Row) => {
    {setShowOptionsPopup({message: "Do you really want to delete the agreement? ", 
    show: true, 
    variant: "confirm", 
    buttons: [{name: "Confirm", color: 'primary', onClick:() => deleteProductAgreement(row) },{name: "Cancel", color: 'secondary', onClick:cancelOptionPopup }]})}
  }

  const deleteProductAgreement = async (row: Row) => {
    const data= {
      practiceProductId : row?.practiceProductId,
      canDeleteAgreement : row?.canDeleteAgreement ?true:false,
    }
    const response = await dispatch(deleteProductAgreementById(data));
   if(response.payload.message === 'SUCCESS') {
      let successObj: SingleAlertInfo = {
        message: "The agreement is successfully deleted.",//CS-1628 - minor message change
        alertType: "success"
      }
      dispatch(setSingleAlertObj(successObj));
    }
    
    searchAgreementsList(searchCriteria);
    cancelOptionPopup();
  }

  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  }

  const handleSignAction = (row: Row) => {
    if (row.agreementGroupId) {
      setShowOptionsPopup({
        message: "This will sign all agreements in this group (including all providers, if Individual). Continue?",
        show: true,
        variant: "confirm",
        buttons: [
          { name: "Confirm", color: "primary", onClick: () => {
            cancelOptionPopup();
            navigate(`/sign_product_agreement?practiceProductId=${row.practiceProductId}`);
          }},
          { name: "Cancel", color: "secondary", onClick: cancelOptionPopup }
        ]
      });
      return;
    }
    navigate(`/sign_product_agreement?practiceProductId=${row.practiceProductId}`);
  }

  const handleMenuClose = () => {
    setAnchorElMenu(null);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenu(event.currentTarget);
  };

  const handlePreviewAction = async (row: Row) => {
    const actionResult = await dispatch(previewAgreement(row.practiceProductId));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      const productLabel = row.productName || (typeof row.product === "string" ? row.product : "Product");
      setShowPDF({ isShow: true, productName: productLabel, fileName: productLabel + "_Product_Agreement" });
    }
  }

  // CS-2070 - Open the audit trail for this specific agreement. The pricing-override audit is stored under
  // entityName "Agreement" keyed by practiceProductId, so we search by that id.
  const handleViewAuditAction = async (row: Row) => {
    if (!row?.practiceProductId) return;
    try {
      const response: any = await dispatch(fetchAuditLogList({
        entityName: "Agreement",
        entityNumber: String(row.practiceProductId),
        currentPage: 0,
        itemsPerPage: 100,
        actionType: ["CREATE"],
        userName: null,
        fromDate: undefined,
        toDate: undefined
      }));

      const auditData = response?.payload?.data?.searchAuditData?.content ?? [];
      const transformedData: AuditLogData[] = auditData.map((log: any) => ({
        entityNumber: log.entityNumber ?? String(row.practiceProductId),
        entityName: log.entityName ?? "Agreement",
        action: log.action ?? "",
        auditedObject: log.auditedObject,
        actionType: log.actionType ?? "",
        clientIP: log.clientIP ?? "",
        auditUser: log.auditUser,
        auditTimestamp: log.auditTimestamp ?? "",
        userName: log.userName ?? log.auditUser?.userName ?? "Unknown User"
      }));

      setAuditLogData(transformedData);
      setAuditLogTitle(`Audit Trail — ${row.product}`);
      setShowAuditLogPopup(true);
    } catch (error) {
      setAuditLogData([]);
      setAuditLogTitle(`Audit Trail — ${row.product}`);
      setShowAuditLogPopup(true);
    }
  };

  const handleCloseAuditLog = () => {
    setShowAuditLogPopup(false);
    setAuditLogData([]);
  };
  // CS-1555 - Start
  useEffect(() => {
    const saved = sessionStorage.getItem("activeActionButtonId");
    if (saved) {
      setActiveButtonId(saved);
    }
  }, []);

  const handleActionButtonClick = (buttonId: string, originalClick: () => void) => {
    const currentPageKey = "agreementList";
    saveSelectionForPage(currentPageKey, buttonId);
    setActiveButtonId(buttonId);
    sessionStorage.setItem("activeActionButtonId", buttonId);
    originalClick();
  };

  const handleRowNavigateToPdf = (row: TableRow) => {
    if (!row || typeof row !== "object") return;
    if (!("practiceProductId" in row) || !("status" in row)) return;

    const agreementRow = row as Row;
    if (!canPreviewAgreement) return;
    if (agreementRow.status === AGREEMENT_STATUS.DELETED) return;
    handleActionButtonClick(`productAgreementListActionButton-${agreementRow.practiceProductId}`, () =>
      handlePreviewAction(agreementRow)
    );
  };
  // CS-1555 - End

  const handleInitiateClick = async (row: Row) => {

    {
      setShowOptionsPopup({
        message: row.agreementGroupId
          ? "This will initiate all agreements in this group (including all providers, if Individual). Continue?"
          : `Do you really want to initiate agreement? `,
        show: true,
        variant: "confirm",
        buttons: [{ name: "Confirm", color: 'primary', onClick: () => initiateSingleAgreement(row) }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
      })
    };
  }

  const initiateSingleAgreement = async (row : Row) => {
    const response = await dispatch(initiateProductAgreements([row.practiceProductId]));
    if (response.meta.requestStatus === 'fulfilled') {
      let successObj: SingleAlertInfo = {
        message: "Agreement successfully initiated.",
        alertType: "success"
      };
      dispatch(setSingleAlertObj(successObj));
    }
    
    searchAgreementsList(searchCriteria);
    cancelOptionPopup()
  }

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>, command: string, row: PracticeProductAgreement | null) => {
    const {checked} = e.target;
    if(checked){
      if(command === "ALL" && productAgreementsList){
          setIsCheck(prevValues=> {
            const agreementIdList = productAgreementsList.content.map(agreement => {
              return agreement.practiceProductId;
            });
            return agreementIdList;
          });
        setIsCheckAll(true);
      } else if(command !== "ALL") {
        row && setIsCheck(prevValues => {
          const newValues = [...prevValues];
          newValues.push(row.practiceProductId);
          return newValues
        })
      };
    } else {
      if(command === 'ALL'){
        setIsCheck([]);
        setIsCheckAll(false);
      } else {
        if(row){
          setIsCheck(isCheck.filter(item => item !== row.practiceProductId))
        }
      }
    }
  }

  const submitInactiveDate = async (date: string) => {
    const selectedDate = dayjs(date);
    //@ts-ignore
    if (dayjs(showInactivePopup?.productAgreementObject?.effectiveDate).isAfter(selectedDate)) {
      let errObj: SingleAlertInfo = {
        message: "Please select a date after the effective date.",
        alertType: "error"
      };
      dispatch(setSingleAlertObj(errObj));
    } else {
      const practiceProductId = showInactivePopup.productAgreementObject.practiceProductId;
      if (!practiceProductId) {
        return;
      }
      const submitObj: { id: number; inactiveDate: string } = { id: practiceProductId, inactiveDate: date };
      const response = await dispatch(inactivateProductAgreementsById({ inactiveData: submitObj, interceptorOption: { skipErrorPopup: true } }));

      if (response?.payload?.code === RESPONSE_CODES.SUCCESS_CODE) {
        let successObj: SingleAlertInfo = {
          message: "The agreement inactivate date was added successfully.",
          alertType: "success"
        };
        dispatch(setSingleAlertObj(successObj));

        searchAgreementsList(searchCriteria);
        setShowInactivePopup({ isShow: false, productAgreementObject: {} });
      } else if (response?.payload?.code === RESPONSE_CODES.FAILED_CODE) {
        let errObj: SingleAlertInfo = {
          message: response.payload?.message || "Active agreement already exists.",
          alertType: "error"
        }
        dispatch(setSingleAlertObj(errObj));
      }
    }
  }

  // CS-1288 START
  const handleInitiateMultipleClick = async () => {
    // Only allow agreements with status === HELD to be initiated
    const selectedRows = formattedPracticeInfo?.filter(row => isCheck.includes(row.practiceProductId)) || [];
    const ineligibleCount = selectedRows.filter(row => row.status !== AGREEMENT_STATUS.HELD || dayjs(row.inactiveDate).isBefore(dayjs(),'day')).length;
    if (ineligibleCount > 0) {
      dispatch(setSingleAlertObj({
        message: `You have selected ${ineligibleCount} agreement(s) which are not available to initiate.`,
        alertType: "error"
      }));
      return;
    }
    setShowOptionsPopup({
      message: `Do you really want to initiate ${selectedRows.length} agreement(s)? `,
      show: true,
      variant: "confirm",
      buttons: [
        { name: "Confirm", color: 'primary', onClick: () => initiateMultipleAgreements() },
        { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }
      ]
    });
  }
  // CS-1288 END

  const initiateMultipleAgreements = async () => {
    let heldAgreementIdList = getHeldAgreementList();
    if(heldAgreementIdList){
      const response = await dispatch(initiateProductAgreements(heldAgreementIdList));
      if(response.meta.requestStatus === 'fulfilled') {
        let successObj: SingleAlertInfo = {
          message: `${heldAgreementIdList.length} Agreement(s) successfully initiated.`,
          alertType: "success"
        };
        dispatch(setSingleAlertObj(successObj));
      }

      searchAgreementsList(searchCriteria);
    };
    cancelOptionPopup();
  }

  const getHeldAgreementList = () => {
    let heldAgreementIdList: (number | undefined)[]= [];
    if (isCheck && isCheck.length > 0) {
      let heldAgreementList = isCheck?.map(practiceProductId => {
        const heldAgreement = productAgreementsList?.content.find((agreement : PracticeProductAgreement) => agreement.practiceProductId === practiceProductId);
        return heldAgreement;
      });
      heldAgreementList = heldAgreementList?.filter(agreement => agreement?.agreementStatus === AGREEMENT_STATUS.HELD);
      heldAgreementIdList = heldAgreementList?.map(agreement => agreement?.practiceProductId);
    }
    return heldAgreementIdList;
  }

  const handleGenerateClick = async () => {
    const response = await dispatch(getAgreementCountForExpiredProductPrice());
    if (response?.payload) {
      setShowOptionsPopup({message: `${response.payload}`, 
      show: true, 
      variant: "confirm", 
      buttons: [{name: "Confirm", color: 'primary', onClick:() => generateAgreements() },{name: "Cancel", color: 'secondary', onClick:cancelOptionPopup }]});
    }
  }

  const handleExport = () => {
    const searchCriteriaFromStorage = getSearchCriteriaFromSessionStorage(
      CAN_ACCESS_APPLICATION.PRODUCT_AGREEMENT +
      "SearchCriteria" +
      userInfo.userId
    );

    if (searchCriteriaFromStorage) {
      let dataToSearch = {
        ...searchCriteria,
        currentPage: searchCriteriaFromStorage.currentPage,
        downloadState: true,
      };

      if (!dataToSearch.practiceIds || dataToSearch.practiceIds.length === 0) {
        dataToSearch.practiceIds = practiceByUserRole && practiceByUserRole.length > 0
          ? practiceByUserRole?.map(practice => Number(practice.practiceId)) : [];
      }
      if (!dataToSearch.productIds || dataToSearch.productIds.length === 0) {
        dataToSearch.productIds = productsByUserRoleAgreementSlice && productsByUserRoleAgreementSlice.length > 0
          ? productsByUserRoleAgreementSlice?.map(product => Number(product.productId)) : [];
      }
      if (!dataToSearch.agreementStatus || dataToSearch.agreementStatus.length === 0) {
        dataToSearch.agreementStatus = statusOptions && statusOptions.length > 0 ? statusOptions?.map(status => status.value) : [];
      }
      if (!dataToSearch.agreementValidity || dataToSearch.agreementValidity.length === 0) {
        dataToSearch.agreementValidity = validityOptions && validityOptions.length > 0 ? validityOptions?.map(status => status.value) : [];
      }

      ExportToExcel(
        dataToSearch,
        columns,
        dispatch,
        EXPORTABLE_ITEMS.AGREEMENT
      );

    }
  };

  // CS-1288 START
  const handleInactiveAgreements = async () => {
    // Only allow agreements with inactiveDate == null or empty string or undefined to be inactivated
    const selectedRows = formattedPracticeInfo?.filter(row => isCheck.includes(row.practiceProductId)) || [];
    const ineligibleCount = selectedRows.filter(
      row => (row.inactiveDate !== null && row.inactiveDate !== "") || row.status === AGREEMENT_STATUS.DELETED 
    ).length;
    if (ineligibleCount > 0) {
      dispatch(setSingleAlertObj({
        message: `You have selected ${ineligibleCount} agreement(s) which are not available to inactivate.`,
        alertType: "error"
      }));
      return;
    }
    setShowBulkInactivePopup({ isShow: true, ids: selectedRows.map(row => row.practiceProductId) });
  }

  const handleCreateAgreements = async () => {
  const selectedRows = formattedPracticeInfo?.filter(row => isCheck.includes(row.practiceProductId)) || [];

  const ineligibleRows = selectedRows.filter(
    row =>
      row.inactiveDate === null ||
      !dayjs(row.inactiveDate).isBefore(dayjs(), 'day') ||
      row.status === AGREEMENT_STATUS.DELETED
  );

  if (ineligibleRows.length > 0) {
  const message = (
    <div>
      <div>To Create Mass Agreement, Please uncheck below mentioned active agreement and try again.</div>
      <ul style={{ paddingLeft: '20px', margin: 0 }}>
        {ineligibleRows.map(row => {
          const effectiveDate = row.effectiveDate ? dayjs(row.effectiveDate).format('MM/DD/YYYY') : 'N/A';
          const hasProvider = row.provider && row.provider.trim() !== '';
          console.log("%c Line:599 🍺 row.provider", "color:#465975", row.provider);
          return (
            <li key={row.practiceProductId}>
              {hasProvider  ? ` ${row.practice} - ${row.product}` : `${row.practice}  ${row.product}`} 
              (Effective: {effectiveDate})
            </li>
          );
        })}
      </ul>
    </div>
  );
    dispatch(setSingleAlertObj({
      message,
      alertType: "error"
    }));
    return;
  }

  setShowOptionsPopup({
    message: `Do you really want to create ${selectedRows.length} agreement(s)?`,
    show: true,
    variant: "confirm",
    buttons: [
      { name: "Confirm", color: 'primary', onClick: () => createBulkAgreements(selectedRows.map(row => row.practiceProductId)) },
      { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }
    ]
  });
  };


  const createBulkAgreements = async (practiceProductIds: number[]) => {
    cancelOptionPopup();
    const response = await dispatch(createMassAgreements({ ids: { practiceProductIds: practiceProductIds }, interceptorOption: { skipErrorPopup: true } }));
    
    setShowProductAgreementPopup(true)

    searchAgreementsList(searchCriteria);
  }
  // CS-1288 END

    const generateAgreements = async () => {
    const response = await dispatch(generateNewAgreementsForExpiredProductPrice());
    if(response.meta.requestStatus === 'fulfilled') {
      let successObj: SingleAlertInfo = {
        message: `Agreements successfully generated.`,
        alertType: "success"
      }
      dispatch(setSingleAlertObj(successObj));
    }
    cancelOptionPopup();

    searchAgreementsList(searchCriteria);
  }

  // CS-1288 START
  //CS-1378 - Start
  interface Column {
    id: string;
    label: string | JSX.Element;
    minWidth?: number;
    width?: number;
    padding?: string;
    align?: "right" | "center" | "left";
    format?: (value: number) => string;
  }

  const columns: Column[] = [
    {
      id: "checkboxes",
      label: (
        <Box sx={agreementCheckboxSx}>
          <CustomCheckbox
            control={control}
            // color="primary"
            checked={
              formattedPracticeInfo && formattedPracticeInfo.length > 0 && (
                (() => {
                  const activeRows = formattedPracticeInfo.filter(row => row.status !== AGREEMENT_STATUS.DELETED);
                  return activeRows.length > 0 && isCheck.length === activeRows.length;
                })()
              )
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const checked = e.target.checked;
              if (checked && formattedPracticeInfo) {
               setIsCheck(
                formattedPracticeInfo
                  .filter(row => row.status !== 'DELETED')
                  .map(row => row.practiceProductId)
              );
                setIsCheckAll(true);
              } else {
                setIsCheck([]);
                setIsCheckAll(false);
              }
            }}
            />
        </Box>
      ),
      minWidth: 36,
      width: 42,
      padding: "0 4px",
    },
    { id: "product", label: "Product", minWidth: 100 },
    { id: "manufacturerIds", label: "Manufacturer", minWidth: 100 }, //CS-1625
    { id: "practice", label: "Practice", minWidth: 100 },
    { id: "provider", label:"Provider", minWidth: 100},
    { id: "effectiveDate", label: "Eff. Date", minWidth: 90 },
    { id: "inactiveDate", label: "Inact. Date", minWidth: 90},
    { id: "fileType", label: "File Type", minWidth: 100 },
    { id: "discountType", label: "Discount", minWidth: 100},
    { id: "extendedPrice", label: "Ext. Price", minWidth: 90 },
    { id: "agreementType", label: "Agr. Type", minWidth: 90 },
    { id: "status", label: "Status", minWidth: 100 },
    { id: "auditTimestamp", label: "Time", minWidth: 100 },
    { id: "action", label: "Action", minWidth: 50 }
  ];

  useEffect(() => {
    if (!productAgreementsList) return;

    const uniqueAgreements = new Map<number, PracticeProductAgreement>();

    productAgreementsList.content.forEach((agreement: PracticeProductAgreement) => {
      if (!uniqueAgreements.has(agreement.practiceProductId)) {
        uniqueAgreements.set(agreement.practiceProductId, agreement);
      }
    });

    const rows: Row[] = Array.from(uniqueAgreements.values()).map((agreement) => ({
      checkboxes: checkboxComponent("", agreement),
      productId: agreement.productId,
      manufacturerIds: agreement.manufacturerName, //CS-1625
      practiceProductId: agreement.practiceProductId,
      product: agreement.productName,
      productName: agreement.productName,
      agreementGroupId: agreement.agreementGroupId,
      agreementType: agreement.agreementGroupId ? "Multi Product" : "Single Product",
      practice: `${agreement.practiceName || ""}${agreement.providerName ? " - " + agreement.providerName : ""}`,
      effectiveDate: agreement.effectiveDate,
      fileType: agreement.fileType,
      discountType: agreement.hasSKUPrice
        ? "-"
        : agreement.discountType !== "CUSTOM"
          ? `${agreement.discountType} %`
          : agreement.discountType,
      inactiveDate: agreement.inactiveDate,
      extendedPrice: agreement.hasSKUPrice
        ? (
            <Link
              component="button"
              underline="hover"
              variant="body2"
              onClick={(e) => {
                e.stopPropagation();
                handleActionButtonClick(`productAgreementListActionButton-${agreement.practiceProductId}`, () => handleOpenSkuDialog(agreement)); // CS-1555
              }}
              color={colors.info[300]}
            >
              SKU Price
            </Link>
          )
        : <CustomCurrencyField externalValue={agreement.extendedPrice} displayType="text" />,
      status: agreement.agreementStatus,
      auditTimestamp: agreement.auditTimestamp,
      provider: agreement.providerName,
      //CS-1628
      canDeleteAgreement: agreement.canDeleteAgreement,
    }));

    const finalRows =
      userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN
        ? rows
        : rows.filter((row) => row.status !== AGREEMENT_STATUS.DELETED);

    setFormattedPracticeInfo(finalRows);
  }, [productAgreementsList, isCheck]);

  // SKU Dialog Table Columns
  const skuDialogColumns: Column[] = [
    { id: "productItemSku", label: "SKU", minWidth: 100 },
    { id: "productItemDescription", label: "Description", minWidth: 100 },
    { id: "discountType", label: "Discount", minWidth: 100 , align: 'center'},
    { id: "extendedPrice", label: "Extended Price", minWidth: 100 , align: 'right'},
  ];

  // Handler to open SKU dialog
  const handleOpenSkuDialog = async (agreement: PracticeProductAgreement) => {
    const skuData: SKUProductAgreementRequest = {
      productId: agreement.productId,
      practiceProductId: agreement.practiceProductId,
    };

    await dispatch(fetchSKUProductAgreementList(skuData));
    setSkuDialogTitle(`SKU Price Details - ${agreement.productName}`);
    setShowSkuDialog(true);
  };

  const skuDialogRows = useMemo(() => {
    if (!skuProductAgreementList || skuProductAgreementList.length === 0) {
      return [];
    }
    return skuProductAgreementList.map((sku:any) => ({
      productItemSku: sku.productItemSku,
      productItemDescription: sku.productItemDescription,
      discountType: sku.discountType !== "CUSTOM" ? `${sku.discountType} %` : sku.discountType,
      extendedPrice: <CustomCurrencyField externalValue={sku.extendedPrice} displayType="text" />,
    }));
    },[skuProductAgreementList]);
  // CS-1288 END

  const actionComponent = (row: Row): JSX.Element => {
    return (
      <>
        <CustomIconButton color="info" size="small" variant="outlined" style={{ display: `${hideElement(row.status === AGREEMENT_STATUS.DELETED || !canPreviewAgreement)}` }}
          onClick={() => handleActionButtonClick(`productAgreementListActionButton-${row.practiceProductId}`, () => handlePreviewAction(row))} tooltipMessage="PDF" tooltipPlacement='top' // CS-1555
          ><PictureAsPdfOutlined fontSize="small" /></CustomIconButton>
        <CustomIconButton color="success" size="small" variant="outlined" onClick={() => handleActionButtonClick(`productAgreementListActionButton-${row.practiceProductId}`, () => handleSignAction(row))} // CS-1555
          style={{
            display: `${hideElement(
              !(row.status === AGREEMENT_STATUS.INITIATED || row.status === AGREEMENT_STATUS.PRACTICE_SIGNED)
              || !(canPracticeSignAgreement || canManufacturerSignAgreement)
              || (canPracticeSignAgreement && row.status === AGREEMENT_STATUS.PRACTICE_SIGNED)
              || (canManufacturerSignAgreement && (row.status === AGREEMENT_STATUS.INITIATED))
              ||(row.inactiveDate !== null && dayjs(row.inactiveDate).isBefore(dayjs(), 'day'))
            )}`
          }}
          tooltipMessage={(row.status === AGREEMENT_STATUS.INITIATED) ? 'Practice Sign' : (row.status === AGREEMENT_STATUS.PRACTICE_SIGNED) ? 'Manufacturer Sign' : ''}
          tooltipPlacement='top'><DriveFileRenameOutline fontSize="small" /></CustomIconButton>
       <CustomIconButton
          color="success"
          size="small"
          variant="outlined"
          onClick={() => handleActionButtonClick(`productAgreementListActionButton-${row.practiceProductId}`, () => handleInitiateClick(row))} // CS-1555  
          style={{
            display: `${hideElement(
              !canInitiateAgreement ||
              row.status !== AGREEMENT_STATUS.HELD ||
              (row.inactiveDate !== null && dayjs(row.inactiveDate).isBefore(dayjs(), 'day'))
            )}`,
          }}
          tooltipMessage="Initiate"
          tooltipPlacement="top"
        >
          <FormatItalicOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton color='info' size="small" variant="outlined" onClick={() => handleActionButtonClick(`productAgreementListActionButton-${row.practiceProductId}`, () => setShowUploadSignedAgreementPopup({ show: true, practiceProductId: row.practiceProductId }))} // CS-1555
          style={{ display: `${hideElement(!canUploadAgreement || row.status === AGREEMENT_STATUS.EXECUTED || row.status === AGREEMENT_STATUS.DELETED 
          || (row.inactiveDate !== null && dayjs(row.inactiveDate).isBefore(dayjs(), 'day')) )}` }}
          tooltipMessage="Upload Signed Agreement" tooltipPlacement="top"
        ><DriveFolderUpload fontSize="small" /></CustomIconButton>
         <CustomIconButton
          color="warning" size="small" variant="outlined"
          style={{
            display: `${hideElement(dayjs(row.inactiveDate).isValid() || !canInactivateAgreement)}`
          }}
          onClick={() => handleActionButtonClick(`productAgreementListActionButton-${row.practiceProductId}`, () => handleInactiveAction(row))} // CS-1555
          tooltipMessage="Inactivate" tooltipPlacement='top'><DoDisturbAltOutlined fontSize="small" /></CustomIconButton>
        {/* CS-1628 - Replacing these new logic for delete button to delete only unreferenced agreements from now */}
        <CustomIconButton color="error" size="small" variant="outlined"
          style={{ display: `${hideElement(!(row.canDeleteAgreement && canDeleteAgreement))}` }}
          onClick={() => handleActionButtonClick(`productAgreementListActionButton-${row.practiceProductId}`, () => handleDeleteAction(row))} // CS-1555
          tooltipMessage="Delete" tooltipPlacement='top'><DeleteOutlineOutlined fontSize="small" /></CustomIconButton>
        {/* CS-2070 - View per-agreement audit trail (token-gated: Super Admin / Admin only) */}
        <CustomIconButton color="warning" size="small" variant="outlined"
          style={{ display: `${hideElement(!canViewAgreementAudit)}` }}
          onClick={() => handleActionButtonClick(`productAgreementListActionButton-${row.practiceProductId}`, () => handleViewAuditAction(row))}
          tooltipMessage="View Audit" tooltipPlacement='top'><TrackChanges fontSize="small" /></CustomIconButton>
      </>
    );
  }
  // CS-1288 START
  const checkboxComponent = (command: string, row: PracticeProductAgreement | null): JSX.Element => {
    // Only show checkbox if status is not DELETED and inactiveDate is null/empty/undefined
    const shouldShowCheckbox =
      row &&
      row.agreementStatus !== AGREEMENT_STATUS.DELETED  //CS-1288

    // CS-1555 - Start
    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (command === "ALL") {
        handleCheckbox(e, command, row);
      } else if (row) {
        handleActionButtonClick(
          `productAgreementListActionButton-${row.practiceProductId}`,
          () => handleCheckbox(e, command, row)
        );
      }
    };
    return (
      //CS-1378 - Alignment task
      <Box sx={{ ...agreementCheckboxSx, display: shouldShowCheckbox ? undefined : "none" }}>
        <CustomCheckbox
          control={control}
          // color="primary"
          checked={command === 'ALL' ? isCheckAll : command === "" && row ? isCheck.includes(row.practiceProductId) : undefined}
          onChange={handleCheckboxChange}
        />
      </Box>
    );
    // CS-1555 - End
  }

  // Add the submitBulkInactiveDate function for bulk inactivation
  const submitBulkInactiveDate = async (date: string) => {
    let hasError = false;
    for (const id of showBulkInactivePopup.ids) {
      const row = formattedPracticeInfo?.find((r) => r.practiceProductId === id);
      if (row && dayjs(row.effectiveDate).isAfter(dayjs(date))) {
        hasError = true;
        break;
      }
    }
    if (hasError) {
      dispatch(setSingleAlertObj({ message: "Please select a date after the effective date for all selected agreements.", alertType: "error" }));
      return;
    }
    await dispatch(bulkInactivateProductAgreementByIds({ inactiveData: { ids: showBulkInactivePopup.ids, inactiveDate: date }, interceptorOption: { skipErrorPopup: true } }));
    dispatch(setSingleAlertObj({ message: "Selected agreements inactivated successfully.", alertType: "success" }));
    setShowBulkInactivePopup({ isShow: false, ids: [] });
    // Ensure the list is refreshed after the operation completes
    await searchAgreementsList(searchCriteria);
  };
  // CS-1288 END

  useEffect(()=>{
    // Hide loading dialog whenever productAgreementsList changes (including null/undefined)
    dispatch(showHideLoadingDialog(false))
  },[productAgreementsList])

  const totalRecords = productAgreementsList?.totalElements ?? 0;

  return (
    <>
      {showUploadSignedAgreementPopup.show && <UploadSignedAgreementPopup
        open={showUploadSignedAgreementPopup.show}
        onClose={() => {
          setShowUploadSignedAgreementPopup({ show: false, practiceProductId: null });
          searchAgreementsList(searchCriteria);
        }}
        practiceProductId={showUploadSignedAgreementPopup.practiceProductId}
      />}
      {/* CS-2070 - Per-agreement audit trail popup */}
      <AuditLogDisplayPopup
        open={showAuditLogPopup}
        onClose={handleCloseAuditLog}
        title={auditLogTitle}
        auditLogData={auditLogData}
      />
      { showOptionPopup.show &&
        < OptionsPopup
          open={showOptionPopup.show}
          onClose={() => setShowOptionsPopup({ message: "", show: false })}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      }
      { showPDF.isShow &&
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() => setShowPDF({ isShow: false, productName: "", fileName: "" })}
          title={showPDF.productName + " Product Agreement"}
          fileName={showPDF.fileName}
          base64String={document?.content || ""}
        />
      }
      { showInactivePopup?.isShow &&
        <DatePickerPopup
          open={showInactivePopup?.isShow}
          onClose={() => setShowInactivePopup({ isShow: false, productAgreementObject: {} }) }
          title={"Set Inactive Date"} label={""}
          submitDate={submitInactiveDate}
          //@ts-ignore
          maxDate={ dayjs(showInactivePopup.productAgreementObject?.effectiveDate).add(20, 'year') }
        />
      }
      {/* CS-1288  */}
      {showProductAgreementPopup &&(
      <>
      <ProductAgreementPopup
        open={showProductAgreementPopup}
        onClose={() => setShowProductAgreementPopup(false)}
      />
      </>
      )}
      {/* CS-1288  */}
      {/* SKU Price Dialog — unchanged */}
      <Dialog
        open={showSkuDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setShowSkuDialog(false)}
        aria-describedby="sku-dialog-slide-description"
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{ sx: paperSx(isMobile) }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
          <Box sx={headerSx}>
            <Box sx={iconBoxSx(modalTokens.accent)}>
              <PictureAsPdfOutlined sx={{ fontSize: "1rem", color: modalTokens.accent }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={700} sx={{ fontSize: "1rem", color: modalTokens.textPrimary, lineHeight: 1.3 }}>
                {skuDialogTitle}
              </Typography>
              <Typography id="sku-dialog-slide-description" sx={{ fontSize: "0.75rem", color: modalTokens.textSecondary, mt: 0.15 }}>
                Review SKU pricing details
              </Typography>
            </Box>
            <IconButton onClick={() => setShowSkuDialog(false)} size="small" sx={closeIconSx}>
              <Close sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Box>
          <Box
            sx={{
              ...bodySx,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarGutter: "stable",
              "&::-webkit-scrollbar": { width: 6, height: 6 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#CBD5E1", borderRadius: 4 },
            }}
          >
            <Box p={1}>
              <Box
                sx={{
                  "& .MuiTableContainer-root::-webkit-scrollbar": { width: 6, height: 6 },
                  "& .MuiTableContainer-root::-webkit-scrollbar-track": { background: "transparent" },
                  "& .MuiTableContainer-root::-webkit-scrollbar-thumb": { background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#CBD5E1", borderRadius: 4 },
                }}
              >
                <Table
                  columns={skuDialogColumns}
                  rows={skuDialogRows}
                  disablePagination={true}
                  borderBox={false}
                  showMenu={false}
                  emptyText="No SKU Price(s) Found"
                />
              </Box>
            </Box>
          </Box>
          <Box sx={footerSx}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
              <Button
                sx={{ ...cancelBtnSx, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 0.8, px: 3, py: 1.2, border: "1px solid", cursor: "pointer" }}
                onClick={() => setShowSkuDialog(false)}
                startIcon={<Cancel />}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* ── Page wrapper ── */}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          px: { xs: 1, md: 3 },
          py: 2,
        }}
      >
        {/* ── Filter panel ── */}
        <Box sx={{ position: "relative", zIndex: 20, overflow: "visible" }}>
          <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
            <Card sx={filterPanelSx}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="flex-end">
                  {/* CS-1365 */}
                  <Grid item md={2} xs={12}>
                    <CustomMultipleDropDown
                      name="productIds"
                      label="Product"
                      placeholderText="Select product"
                      disabled={productOptions && productOptions.length === 1}
                      options={productOptions}
                      control={control}
                      size="small"
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <CustomMultipleDropDown
                      name="practiceIds"
                      label="Practice"
                      placeholderText="Select practice"
                      disabled={practiceOptions && practiceOptions.length === 1}
                      options={practiceOptions}
                      control={control}
                      size="small"
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <CustomMultipleDropDown
                      name="agreementStatus"
                      label="Status"
                      placeholderText="Select status"
                      options={statusOptions}
                      control={control}
                      size="small"
                    />
                  </Grid>
                  {/* CS-1288 */}
                  <Grid item md={2} xs={12}>
                    <CustomMultipleDropDown
                      name="agreementValidity"
                      label="Validity"
                      placeholderText="Select validity"
                      options={validityOptions}
                      control={control}
                      size="small"
                    />
                  </Grid>
                  {/* CS-1625 */}
                  <Grid item md={2} xs={12}>
                    <CustomMultipleDropDown
                      name="manufacturerIds"
                      label="Manufacturer"
                      placeholderText="Select manufacturer"
                      options={manufacturesList}
                      control={control}
                      size="small"
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <CustomMultipleDropDown
                      name="agreementTypes"
                      label="Agreement Type"
                      placeholderText="Select agreement type"
                      options={agreementTypeOptions}
                      control={control}
                      size="small"
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <Button
                      fullWidth
                      size="large"
                      startIcon={<SearchOutlined />}
                      sx={{ ...primaryActionBtnSx, height: "42px", py: 0 }}
                      type="submit"
                      removeUpperCase
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Collapse>
        </Box>

        {/* ── Main list card ── */}
        <Box mt={2} sx={{ position: "relative", zIndex: 1 }}>
          <Card sx={{ ...listCardSx, p: { xs: "14px !important", md: "18px !important" } }}>
            <Grid container justifyContent="flex-end" alignItems="center" spacing={1.2}>
              <Grid item xs={12} md>
                <Typography variant="h3" fontWeight={800} color="text.primary">Agreement List</Typography>
              </Grid>

              {/* Show / Hide filters */}
              <Grid item xs={12} md="auto">
                <Button
                  variant="outlined"
                  color="info"
                  onClick={toggleFilterPanel}
                  startIcon={<SearchOutlined />}
                  fullWidth
                  sx={outlineBtnSx}
                  removeUpperCase
                >
                  {showCard ? "Hide Filters" : "Show Filters"}
                </Button>
              </Grid>

              {/* Initiate Agreement(s) */}
              <Grid item xs={12} sm="auto">
                <Button
                  startIcon={<FormatItalicOutlined />}
                  style={{ display: `${hideElement(userInfo.entityName !== USER_ROLE_ENTITY_NAMES.ADMIN)}` }}
                  disabled={isCheck && isCheck.length === 0}
                  onClick={handleInitiateMultipleClick}
                  fullWidth
                  sx={initiateAgreementBtnSx}
                  removeUpperCase
                >
                  Initiate Agreement(s)
                </Button>
              </Grid>

              {/* Inactive Agreement(s) */}
              <Grid item xs={12} sm="auto">
                <Button
                  color="warning"
                  startIcon={<DoDisturbAltOutlined />}
                  style={{ display: `${hideElement(!canInactivateAgreement)}` }}
                  disabled={isCheck && isCheck.length === 0}
                  onClick={handleInactiveAgreements}
                  fullWidth
                  sx={inactiveAgreementBtnSx}
                  removeUpperCase
                >
                  Inactive Agreement(s)
                </Button>
              </Grid>

              {/* Create Agreement */}
              <Grid item xs={12} sm="auto">
                <Button
                  startIcon={<AddCircleOutlineOutlined />}
                  onClick={() => navigate("/create_new_agreement")}
                  style={{ display: `${hideElement(userInfo.entityName !== USER_ROLE_ENTITY_NAMES.ADMIN)}` }}
                  fullWidth
                  sx={primaryActionBtnSx}
                  removeUpperCase
                >
                  Create Agreement
                </Button>
              </Grid>

              {/* Export */}
              {canExportList && (
                <Grid item sm="auto">
                  {!isMobile && (
                    <Button
                      id="customized-button"
                      aria-controls={Boolean(anchorElMenu) ? "customized-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={Boolean(anchorElMenu) ? "true" : undefined}
                      variant="outlined"
                      disableElevation
                      onClick={handleMenuClick}
                      startIcon={<ExitToApp />}
                      sx={exportBtnSx}
                      removeUpperCase
                      endIcon={
                        <Box
                          component="span"
                          sx={{
                            display: "flex",
                            transition: "transform 0.3s ease",
                            transform: anchorElMenu ? "rotate(180deg)" : "rotate(0deg)",
                            padding: "2px",
                          }}
                        >
                          <KeyboardArrowDown />
                        </Box>
                      }
                    >
                      Export
                    </Button>
                  )}

                  {isMobile && (
                    <Button
                      color="success"
                      variant="text"
                      onClick={handleMenuClick}
                      startIcon={<DownloadOutlined fontSize="small" />}
                      sx={exportBtnSx}
                      removeUpperCase
                    >
                      Export
                    </Button>
                  )}

                  <StyledMenu
                    id="customized-menu"
                    MenuListProps={{ "aria-labelledby": "customized-button" }}
                    anchorEl={anchorElMenu}
                    open={Boolean(anchorElMenu)}
                    onClose={handleMenuClose}
                    slotProps={{
                      paper: {
                        sx: {
                          width: "260px",
                          maxWidth: "90vw",
                          mt: 0.75,
                          p: 0.75,
                          borderRadius: "12px",
                          border: `1px solid ${isDark ? "rgba(0,184,217,0.3)" : "rgba(0,184,217,0.22)"}`,
                          backgroundColor: isDark ? "#141720" : "#ffffff",
                          backgroundImage: "none",
                          boxShadow: isDark
                            ? "0 18px 42px rgba(0,0,0,0.45)"
                            : "0 14px 32px rgba(15,23,42,0.14)",
                        },
                      },
                    }}
                  >
                    <Box sx={{ px: 1, mb: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        disabled={totalRecords > 1000}
                        startIcon={<CloudDownload />}
                        onClick={() => {
                          const searchCriteriaFromStorage = getSearchCriteriaFromSessionStorage(
                            CAN_ACCESS_APPLICATION.PRODUCT_AGREEMENT + "SearchCriteria" + userInfo.userId
                          );
                          if (searchCriteriaFromStorage) {
                            let dataToSearch = { ...searchCriteria, currentPage: searchCriteriaFromStorage.currentPage, downloadState: true };
                            if (!dataToSearch.practiceIds || dataToSearch.practiceIds.length === 0) {
                              dataToSearch.practiceIds = practiceByUserRole && practiceByUserRole.length > 0 ? practiceByUserRole?.map(practice => Number(practice.practiceId)) : [];
                            }
                            if (!dataToSearch.productIds || dataToSearch.productIds.length === 0) {
                              dataToSearch.productIds = productsByUserRoleAgreementSlice && productsByUserRoleAgreementSlice.length > 0 ? productsByUserRoleAgreementSlice?.map(product => Number(product.productId)) : [];
                            }
                            if (!dataToSearch.agreementStatus || dataToSearch.agreementStatus.length === 0) {
                              dataToSearch.agreementStatus = statusOptions && statusOptions.length > 0 ? statusOptions?.map(status => status.value) : [];
                            }
                            if (!dataToSearch.agreementValidity || dataToSearch.agreementValidity.length === 0) {
                              dataToSearch.agreementValidity = validityOptions && validityOptions.length > 0 ? validityOptions?.map(status => status.value) : [];
                            }
                            ExportToExcel(dataToSearch, columns, dispatch, EXPORTABLE_ITEMS.AGREEMENT_LIST);
                          }
                          handleMenuClose();
                        }}
                        sx={exportMenuItemBtnSx}
                        removeUpperCase
                      >
                        Export Search List
                      </Button>
                    </Box>

                    <Box sx={{ px: 1, mb: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        disabled={totalRecords > 1000}
                        startIcon={<CloudDownload />}
                        onClick={() => { handleExport(); handleMenuClose(); }}
                        sx={exportMenuItemBtnSx}
                        removeUpperCase
                      >
                        Export Details List
                      </Button>
                    </Box>

                    {totalRecords > 1000 && (
                      <Box
                        sx={{
                          mx: 1,
                          p: 1.5,
                          mt: 1,
                          backgroundColor: "warning.light",
                          borderRadius: 1,
                          color: "warning.contrastText",
                        }}
                      >
                        <Typography variant="body2">
                          Export is available only when the list contains 1,000 records or fewer.
                          Maximum export size: 1000 records.
                        </Typography>
                      </Box>
                    )}
                  </StyledMenu>
                </Grid>
              )}
            </Grid>

            <Table
              columns={columns}
              rows={formattedPracticeInfo || []}
              pageNumber={handlePagination}
              totalPages={productAgreementsList?.totalPages}
              totalElementText={"Records: " + (productAgreementsList?.totalElements ? productAgreementsList?.totalElements : "0")}
              currentPageNumber={searchCriteria.currentPage - 1}
              actionComponent={(row: Row) =>
                row.status !== AGREEMENT_STATUS.DELETED ? actionComponent(row) : null
              }
              emptyText="No Product Agreement(s) Found"
              showMenu={true}
              sortableColumns={agreementListSort.sortableColumns}
              serverSideSorting
              onSortChange={handleTableSort}
              controlledSortColumn={agreementListSort.controlledColumn(searchCriteria.sortBy)}
              controlledSortOrder={searchCriteria.sortOrder ?? null}
              // CS-1555 - Start
              activeButtonId={activeButtonId}
              onRowClick={handleRowNavigateToPdf}
              clickableCards={true}
              uniqueValue="practiceProductId"
              currentPageKey="agreementList"
              // CS-1555 - End
              sx={agreementTableSx}
            />
          </Card>
        </Box>
      </Box>

      {showBulkInactivePopup.isShow && (  
        <DatePickerPopup
          open={showBulkInactivePopup.isShow}
          onClose={() => setShowBulkInactivePopup({ isShow: false, ids: [] })}
          title={"Set Inactive Date"}
          label={""}
          submitDate={submitBulkInactiveDate}
        />
      )}
    </>
  );
};

export default ProductAgreement;